// ============================================================
// DESIGN SYSTEM TOKENS - Footer System
// ============================================================
// These tokens ensure consistency across all footer variants
// and make future updates easy via a single source of truth

// SPACING TOKENS
// ----------------------------------------------------------------
export const spacing = {
  xs: "0.25rem",    // 4px
  sm: "0.5rem",     // 8px
  md: "1rem",       // 16px
  lg: "1.5rem",     // 24px
  xl: "2rem",       // 32px
  "2xl": "3rem",    // 48px
  "3xl": "4rem",    // 64px
  "4xl": "6rem",    // 96px
} as const;

// TYPOGRAPHY TOKENS
// ----------------------------------------------------------------
export const typography = {
  sizes: {
    xs: "0.75rem",   // 12px
    sm: "0.875rem",  // 14px
    base: "1rem",    // 16px
    lg: "1.125rem",  // 18px
    xl: "1.25rem",   // 20px
    "2xl": "1.5rem", // 24px
    "3xl": "1.875rem", // 30px
    "4xl": "2.25rem",  // 36px
  },
  weights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeights: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
};

// COLOR TOKENS
// ----------------------------------------------------------------
export const colors = {
  // Background variants
  background: {
    primary: "#0f172a",    // Slate 900
    secondary: "#1e293b",  // Slate 800
    tertiary: "#334155",   // Slate 700
    light: "#f8fafc",      // Slate 50
    white: "#ffffff",
  },
  // Text colors
  text: {
    primary: "#ffffff",
    secondary: "#e2e8f0",  // Slate 200
    muted: "#94a3b8",      // Slate 400
    mutedLight: "#64748b", // Slate 500
    inverse: "#0f172a",    // Slate 900 (for light backgrounds)
  },
  // Accent colors
  accent: {
    primary: "#3b82f6",    // Blue 500
    secondary: "#6366f1",  // Indigo 500
    hover: "#60a5fa",      // Blue 400
    border: "#334155",     // Slate 700
  },
  // Social icon colors
  social: {
    twitter: "#1da1f2",
    linkedin: "#0a66c2",
    github: "#ffffff",
    facebook: "#1877f2",
    instagram: "#e4405f",
    youtube: "#ff0000",
    dribbble: "#ea4c89",
    discord: "#5865f2",
  },
};

// LAYOUT TOKENS
// ----------------------------------------------------------------
export const layout = {
  container: {
    maxWidth: "1280px",    // Standard container
    wide: "1400px",        // Wide container
    padding: "2rem",       // 32px
    paddingMobile: "1rem", // 16px
  },
  grid: {
    columns: {
      2: "repeat(2, minmax(0, 1fr))",
      3: "repeat(3, minmax(0, 1fr))",
      4: "repeat(4, minmax(0, 1fr))",
      5: "repeat(5, minmax(0, 1fr))",
    },
    gap: {
      sm: "1rem",
      md: "2rem",
      lg: "3rem",
      xl: "4rem",
    },
  },
};

// BORDER RADIUS TOKENS
// ----------------------------------------------------------------
export const radius = {
  none: "0",
  sm: "0.25rem",
  md: "0.5rem",
  lg: "0.75rem",
  xl: "1rem",
  full: "9999px",
};

// TRANSITION TOKENS
// ----------------------------------------------------------------
export const transitions = {
  fast: "150ms ease-in-out",
  normal: "250ms ease-in-out",
  slow: "350ms ease-in-out",
};

// SHADOW TOKENS
// ----------------------------------------------------------------
export const shadows = {
  sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
  md: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
  lg: "0 10px 15px -3px rgb(0 0 0 / 0.1)",
  xl: "0 20px 25px -5px rgb(0 0 0 / 0.1)",
};

// RESPONSIVE BREAKPOINTS (Tailwind mapping)
// ----------------------------------------------------------------
export const breakpoints = {
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px",
};

// ICON SIZES
// ----------------------------------------------------------------
export const iconSizes = {
  sm: "1rem",   // 16px
  md: "1.25rem", // 20px
  lg: "1.5rem",  // 24px
  xl: "2rem",    // 32px
};

// ============================================================
// COMPOSED STYLES (Ready-to-use style objects)
// ============================================================

export const footerStyles = {
  // Base footer container
  container: {
    backgroundColor: colors.background.primary,
    color: colors.text.primary,
    paddingTop: layout.container.padding,
    paddingBottom: layout.container.padding,
  },

  // Inner container with max-width
  innerContainer: {
    maxWidth: layout.container.maxWidth,
    margin: "0 auto",
    paddingLeft: layout.container.paddingMobile,
    paddingRight: layout.container.paddingMobile,
  },

  // Grid layouts
  grid2: {
    display: "grid",
    gridTemplateColumns: layout.grid.columns[2],
    gap: layout.grid.gap.lg,
  },
  grid3: {
    display: "grid",
    gridTemplateColumns: layout.grid.columns[3],
    gap: layout.grid.gap.lg,
  },
  grid4: {
    display: "grid",
    gridTemplateColumns: layout.grid.columns[4],
    gap: layout.grid.gap.lg,
  },

  // Responsive grid (mobile first, tablet+, desktop+)
  gridResponsive: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: layout.grid.gap.md,
  },

  // Section spacing
  section: {
    marginBottom: spacing.xl,
  },

  // Link styles
  link: {
    color: colors.text.muted,
    textDecoration: "none",
    transition: transitions.fast,
    fontSize: typography.sizes.sm,
    lineHeight: typography.lineHeights.normal,
  },
  linkHover: {
    color: colors.text.primary,
  },

  // Heading styles
  heading: {
    color: colors.text.primary,
    fontSize: typography.sizes.base,
    fontWeight: typography.weights.semibold,
    marginBottom: spacing.md,
  },

  // Divider
  divider: {
    borderTop: `1px solid ${colors.accent.border}`,
    marginTop: spacing.xl,
    paddingTop: spacing.lg,
  },

  // Bottom bar
  bottomBar: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.md,
    paddingTop: spacing.lg,
    borderTop: `1px solid ${colors.accent.border}`,
  },

  // Social link button
  socialButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "2.5rem",  // 40px
    height: "2.5rem",
    borderRadius: radius.full,
    backgroundColor: colors.background.secondary,
    color: colors.text.muted,
    transition: transitions.normal,
  },
  socialButtonHover: {
    backgroundColor: colors.background.tertiary,
    color: colors.text.primary,
  },

  // Newsletter input
  newsletterInput: {
    width: "100%",
    padding: `${spacing.sm} ${spacing.md}`,
    borderRadius: radius.md,
    border: `1px solid ${colors.accent.border}`,
    backgroundColor: colors.background.secondary,
    color: colors.text.primary,
    fontSize: typography.sizes.sm,
  },
};

// ============================================================
// TYPE EXPORTS
// ============================================================

export type Spacing = keyof typeof spacing;
export type TypographySize = keyof typeof typography.sizes;
export type ColorToken = keyof typeof colors.background | keyof typeof colors.text;
